import page from "@/core/page"
import context from "@/core/context"
import applyFilters from "@/core/filters/filters"
import settings from "@/core/settings"
import measurePerformance from "@/core/measurePerformance"
import { injectDynamicCampaign, removeInjectedCampaign } from "./injection"
import subscribeIntersectionObserver from "./async/intersection"
import subscribeMutationObserver from "./async/mutation"
import { stopHidingDynamicPlacements } from "./placementHiding"
import { DynamicPlacementDTO } from "@/types"

function placements() {
  return settings.placements || {}
}

function availableDynamicPlacement(divIds = Object.keys(placements())) {
  const preview = context.mode.isPreview()
  return divIds.filter(divId => {
    const configuration = placements()[divId]
    if (!configuration || (!configuration.enabled && !preview)) {
      return false
    }
    return configuration.cssSelector && applyFilters(configuration.filters)
  })
}

type HandlePlacement = (e: HTMLElement | null, divId: string, configuration: DynamicPlacementDTO) => boolean | void

/**
 * Abstraction to iterate over dynamic placements:
 * Finds all or subset (divIds) of placements and performs actions upon present / absent placement (handlePlacement)
 *
 * @param {string[]?} divIds
 * @param {function(Element, string, Object):boolean} handlePlacement
 *
 * @return {string[]} divIds of placements that were enabled and deemed worthy by handlePlacement (handlePlacement returned true)
 */
function processPlacementsByCssSelector(handlePlacement: HandlePlacement, divIds?: string[]) {
  return availableDynamicPlacement(divIds).filter(divId => {
    const configuration = placements()[divId]
    const target = page.select(configuration.cssSelector!)
    return handlePlacement(target, divId, configuration)
  })
}

/**
 * Gets a list of placements present on the page and subscribes for new placements to appear
 */
export function getPlacements() {
  return measurePerformance("nosto.get_dynamic_placements", () =>
    processPlacementsByCssSelector((target, divId, configuration) =>
      target
        ? !subscribeIntersectionObserver(target, divId, configuration)
        : subscribeMutationObserver(divId, configuration)
    )
  )
}

export function injectCampaigns(campaigns: Record<string, string>) {
  return measurePerformance("nosto.inject_dynamic_campaigns", () => {
    stopHidingDynamicPlacements()
    const injectArguments: [HTMLElement, DynamicPlacementDTO, string, string][] = []
    const emptyCampaigns: Promise<[string, HTMLElement[]]>[] = []
    // decouple element search from injection so injection doesn't affect applicability of subsequent placements' selector

    // cleanup old campaigns
    // This has been moved up to cover edge-cases where placement targets use, for example :last-child, with "After" manipulation
    // without this processPlacementsByCssSelector would select the placement element instead the actual target
    Object.keys(campaigns).forEach(removeInjectedCampaign)

    processPlacementsByCssSelector((element, divId, config) => {
      // empty string campaign in considered no action
      if (element) {
        if (campaigns[divId]) {
          injectArguments.push([element, config, campaigns[divId], divId])
        } else {
          emptyCampaigns.push(Promise.resolve([divId, []]))
        }
      }
    }, Object.keys(campaigns))
    return Promise.all([...injectArguments.map(args => injectDynamicCampaign(...args)), ...emptyCampaigns])
  })
}

/**
 * Determines, if placement's configuration filters allow
 * placement to be displayed on this page.
 *
 * Use cases: debug toolbar
 */
export function isFiltered(placement: DynamicPlacementDTO) {
  return applyFilters(placement.filters)
}

/**
 * Returns true if filtering configuration was set up for
 * a placement (by url of page type).
 * Use cases: debug toolbar
 */
export function isFilteringConfigured(placement: DynamicPlacementDTO) {
  return (placement.filters || []).length > 0
}
